import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, ArrowDownToLine } from 'lucide-react';
import { posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import ReportExportButton from '@/components/reports/ReportExportButton';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import CashDepositWithdrawModal from '@/components/settings/CashDepositWithdrawModal';
import { format } from 'date-fns';

interface LedgerEntry {
  posting_date: string;
  invoice_no: string;
  payment_no: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

const formatDate = (d?: string) => {
  if (!d) return '';
  try {
    return format(new Date(d), 'd-MMM-yyyy');
  } catch {
    return d;
  }
};

export default function DailyCashLedgerSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);

  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message as any[];
    },
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['cashLedger', from_date, to_date],
    queryFn: async () => {
      const res = await posApi.getCashLedger({ from_date, to_date });
      return res.data.message;
    },
  });

  const entries: LedgerEntry[] = data?.entries || [];
  const opening = data?.opening_balance ?? 0;
  const closing = data?.closing_balance ?? 0;

  const handleExport = () => {
    if (!data) return;
    const lines = [
      'Date,Invoice #,Payment #,Description,Debit,Credit,Balance',
      `,,,Opening Balance,,,${data.opening_balance ?? 0}`,
      ...(data.entries || []).map((row: LedgerEntry) =>
        [
          formatDate(row.posting_date),
          row.invoice_no || '',
          row.payment_no || '',
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.debit || 0,
          row.credit || 0,
          row.balance ?? 0,
        ].join(',')
      ),
      `,,,Closing Balance,,,${data.closing_balance ?? 0}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `cash_ledger_${from_date}_${to_date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-wrap gap-4 justify-between items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />
          <div className="flex gap-2">
            <button 
              onClick={() => setIsDepositModalOpen(true)}
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <ArrowDownToLine className="w-4 h-4" /> Deposit / Withdraw
            </button>
            <ReportExportButton onClick={handleExport} disabled={!data || isLoading} />
          </div>
        </div>
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {t('reports.loadFailed', 'Failed to load report data')}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 gap-4 max-w-lg">
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-500">
              Opening Balance
            </p>
            <p className="text-xl font-bold mt-1 text-gray-900">
              {formatMoney(opening)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-500">
              Closing Balance
            </p>
            <p className={`text-xl font-bold mt-1 ${closing >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatMoney(closing)}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit (In)</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit (Out)</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="inline-flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 rounded-full animate-pulse bg-primary-500" />
                      <div className="w-4 h-4 rounded-full animate-pulse bg-primary-500 animation-delay-200" />
                      <div className="w-4 h-4 rounded-full animate-pulse bg-primary-500 animation-delay-400" />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">{t('reports.loadingLedger', 'Loading ledger...')}</p>
                  </td>
                </tr>
              ) : (
                <>
                  <tr className="bg-gray-50/80">
                    <td className="px-6 py-3 text-sm text-gray-400" colSpan={4}>
                      Opening Balance
                    </td>
                    <td className="px-6 py-3" />
                    <td className="px-6 py-3" />
                    <td className="px-6 py-3 text-sm text-right font-semibold text-gray-900">
                      {formatMoney(opening)}
                    </td>
                  </tr>
                  
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-lg font-medium text-gray-900">No ledger entries</p>
                        <p className="text-sm">There are no cash transactions for the selected date range.</p>
                      </td>
                    </tr>
                  ) : (
                    entries.map((row, i: number) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(row.posting_date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-600 font-medium">
                          {row.invoice_no || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600 font-medium">{row.payment_no || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate" title={row.description}>{row.description || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-emerald-600 font-medium">
                          {row.debit > 0 ? formatMoney(row.debit) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600 font-medium">
                          {row.credit > 0 ? formatMoney(row.credit) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                          {formatMoney(row.balance)}
                        </td>
                      </tr>
                    ))
                  )}
                  
                  <tr className="bg-gray-50/80 border-t border-gray-200">
                    <td className="px-6 py-3 text-sm font-medium text-gray-700" colSpan={4}>
                      Closing Balance
                    </td>
                    <td className="px-6 py-3" />
                    <td className="px-6 py-3" />
                    <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">
                      {formatMoney(closing)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CashDepositWithdrawModal
        open={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
        banks={banks}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ['cashLedger'] });
        }}
      />
    </div>
  );
}
